import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import Alert from '@mui/material/Alert';
import { colorTokens, AppInput } from '@eruvaka/web-pondlogs-base';
import { useLocations } from '../hooks/useLocations';
import { createPond } from '../api/pondsApi';
/* ── Brand palette — references token system ── */
/* ── Native input style (used with Box component="input") ── */
const nativeInputSx = {
    width: '100%',
    px: 1.75,
    py: 1.25,
    borderRadius: '12px',
    fontSize: 14,
    border: `1px solid ${colorTokens.gray200}`,
    outline: 'none',
    color: colorTokens.gray800,
    bgcolor: colorTokens.white,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    '&:focus': { borderColor: colorTokens.primary },
};
/* ── Simulated grid dots for the satellite background ── */
const GRID_DOTS = Array.from({ length: 30 }, (_, i) => {
    const row = Math.floor(i / 6);
    const col = i % 6;
    const types = ['pond', 'pond', 'land', 'land', 'bund'];
    return { x: 12 + col * 15, y: 10 + row * 18, type: types[(row * 7 + col * 3) % types.length] };
});
export const AddPondWizard = memo((props) => {
    const { open, onClose, onSaved } = props;
    const { t } = useTranslation('ponds');
    const { locations, isLoading: isLoadingLocations } = useLocations();
    const [step, setStep] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [pinCoords, setPinCoords] = useState(null);
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [pondName, setPondName] = useState('');
    const [pondArea, setPondArea] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const mapRef = useRef(null);
    const filteredLocations = searchQuery.length > 0
        ? locations.filter((l) => l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (l.city ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (l.state ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
        : locations;
    const handleMapClick = useCallback((e) => {
        if (!mapRef.current)
            return;
        const rect = mapRef.current.getBoundingClientRect();
        const xRatio = (e.clientX - rect.left) / rect.width;
        const yRatio = (e.clientY - rect.top) / rect.height;
        const lat = 14.44 + (0.5 - yRatio) * 0.08;
        const lng = 79.99 + (xRatio - 0.5) * 0.08;
        setPinCoords({ lat: Math.round(lat * 10000) / 10000, lng: Math.round(lng * 10000) / 10000 });
    }, []);
    const handleLocationSelect = (loc) => {
        setSelectedLocationId(loc.id);
        setSearchQuery(`${loc.name}${loc.city ? ` — ${loc.city}` : ''}${loc.state ? `, ${loc.state}` : ''}`);
        setPinCoords({ lat: 14.44 + Math.random() * 0.04, lng: 79.99 + Math.random() * 0.04 });
        setShowSuggestions(false);
    };
    const handleClose = () => {
        setStep(1);
        setSearchQuery('');
        setPinCoords(null);
        setSelectedLocationId('');
        setPondName('');
        setPondArea('');
        setError(null);
        onClose();
    };
    const handleSave = async () => {
        if (!pondName.trim() || !pondArea || !selectedLocationId)
            return;
        const area = parseFloat(pondArea);
        if (isNaN(area) || area <= 0)
            return;
        setIsSaving(true);
        setError(null);
        try {
            const payload = {
                title: pondName.trim(),
                pond_coordinates: pinCoords
                    ? [
                        [pinCoords.lng, pinCoords.lat],
                        [pinCoords.lng + 0.001, pinCoords.lat],
                        [pinCoords.lng + 0.001, pinCoords.lat + 0.001],
                        [pinCoords.lng, pinCoords.lat],
                    ]
                    : [
                        [79.99, 14.44],
                        [79.991, 14.44],
                        [79.991, 14.441],
                        [79.99, 14.44],
                    ],
                timezone: {
                    name: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    offset: new Date().getTimezoneOffset() / -60,
                },
                location_id: selectedLocationId,
                size: area,
                units: 'ha',
            };
            await createPond(payload);
            await onSaved();
            handleClose();
        }
        catch (err) {
            const errCode = err.errorCode ?? '';
            setError(errCode === 'DUPLICATE_PONDS'
                ? 'errors.duplicateName'
                : errCode === 'FORBIDDEN'
                    ? 'errors.permissionDenied'
                    : 'errors.createFailed');
        }
        finally {
            setIsSaving(false);
        }
    };
    const canProceedStep1 = pinCoords !== null && selectedLocationId !== '';
    const canSave = pondName.trim().length > 0 && parseFloat(pondArea) > 0 && !isSaving;
    /* ─── STEP INDICATOR ─── */
    const StepIndicator = () => (_jsx(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${colorTokens.gray200}`,
            bgcolor: colorTokens.gray50,
        }, children: [1, 2].map((s) => {
            const isDone = step > s;
            const isActive = step === s;
            return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Box, { sx: {
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 800,
                            flexShrink: 0,
                            bgcolor: isDone ? colorTokens.secondary : isActive ? colorTokens.primary : 'white',
                            color: isDone || isActive ? 'white' : colorTokens.gray600,
                            border: !isDone && !isActive ? `1px solid ${colorTokens.gray200}` : 'none',
                        }, children: isDone ? '✓' : s }), _jsx(Typography, { sx: {
                            fontSize: 12,
                            fontWeight: 600,
                            color: isActive ? colorTokens.gray800 : colorTokens.gray600,
                        }, children: s === 1 ? t('drawer.stepSelectLocation') : t('drawer.stepPondDetails') }), s < 2 && _jsx(Box, { sx: { width: 32, height: 1, bgcolor: colorTokens.gray200 } })] }, s));
        }) }));
    return (_jsxs(Dialog, { open: open, onClose: handleClose, maxWidth: false, PaperProps: {
            sx: {
                width: 640,
                maxHeight: '90vh',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            },
        }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${colorTokens.gray200}`,
                }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontWeight: 800, fontSize: 16, color: colorTokens.gray800 }, children: t('drawer.createTitle') }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray600, mt: 0.25 }, children: step === 1
                                    ? 'Step 1 of 2 — Select farm location on map'
                                    : 'Step 2 of 2 — Enter pond details' })] }), _jsx(Box, { component: "button", onClick: handleClose, sx: {
                            p: 1,
                            borderRadius: 1,
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            '&:hover': { bgcolor: 'grey.100' },
                        }, children: _jsx(Typography, { sx: { fontSize: 16, color: colorTokens.gray600, lineHeight: 1 }, children: "\u2715" }) })] }), _jsx(StepIndicator, {}), _jsxs(Box, { sx: { flex: 1, overflowY: 'auto' }, children: [step === 1 && (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, p: 2.5 }, children: [_jsxs(Box, { sx: { position: 'relative' }, children: [_jsx(AppInput, { placeholder: "Search farm location\u2026 (e.g. Sunrise Farm, Nellore)", value: searchQuery, onChange: (e) => {
                                            setSearchQuery(e.target.value);
                                            setShowSuggestions(true);
                                        }, onFocus: () => setShowSuggestions(true), disabled: isLoadingLocations, InputProps: {
                                            endAdornment: isLoadingLocations ? _jsx(CircularProgress, { size: 14 }) : null,
                                            sx: { fontSize: 13, borderRadius: '12px' },
                                        } }), showSuggestions && filteredLocations.length > 0 && (_jsx(Box, { sx: {
                                            position: 'absolute',
                                            top: '100%',
                                            mt: 0.5,
                                            left: 0,
                                            right: 0,
                                            bgcolor: 'white',
                                            borderRadius: '12px',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                                            zIndex: 10,
                                            border: `1px solid ${colorTokens.gray200}`,
                                            overflow: 'hidden',
                                        }, children: filteredLocations.map((loc) => (_jsxs(Box, { component: "button", onClick: () => handleLocationSelect(loc), sx: {
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 1.5,
                                                py: 1.25,
                                                fontSize: 12,
                                                textAlign: 'left',
                                                bgcolor: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: colorTokens.gray800,
                                                '&:hover': { bgcolor: 'grey.50' },
                                            }, children: [_jsx(Typography, { sx: { fontSize: 14, color: colorTokens.secondary }, children: "\uD83D\uDCCD" }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 600, color: colorTokens.gray800 }, children: loc.name }), (loc.city || loc.state) && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.gray600 }, children: [loc.city, loc.state].filter(Boolean).join(', ') }))] })] }, loc.id))) }))] }), _jsxs(Box, { ref: mapRef, onClick: handleMapClick, onMouseDown: () => setShowSuggestions(false), sx: {
                                    position: 'relative',
                                    height: 300,
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    border: `1px solid ${colorTokens.gray200}`,
                                    cursor: 'crosshair',
                                    userSelect: 'none',
                                }, children: [_jsx(Box, { sx: {
                                            position: 'absolute',
                                            inset: 0,
                                            background: 'linear-gradient(135deg, #2d5a27 0%, #3a7a32 25%, #4a9a3c 40%, #2d7a5a 55%, #1a5c4a 70%, #2d6a3a 85%, #3a7a32 100%)',
                                        } }), GRID_DOTS.map((d, i) => (_jsx(Box, { sx: {
                                            position: 'absolute',
                                            left: `${d.x}%`,
                                            top: `${d.y}%`,
                                            borderRadius: 1,
                                            width: d.type === 'pond' ? '13%' : d.type === 'bund' ? '15%' : '12%',
                                            height: d.type === 'pond' ? '14%' : d.type === 'bund' ? '2%' : '12%',
                                            bgcolor: d.type === 'pond'
                                                ? 'rgba(100,200,240,0.55)'
                                                : d.type === 'bund'
                                                    ? 'rgba(180,160,100,0.6)'
                                                    : 'rgba(60,120,50,0.3)',
                                            border: d.type === 'pond' ? '1px solid rgba(100,200,255,0.4)' : 'none',
                                        } }, i))), _jsxs("svg", { style: {
                                            position: 'absolute',
                                            inset: 0,
                                            width: '100%',
                                            height: '100%',
                                            opacity: 0.15,
                                            pointerEvents: 'none',
                                        }, children: [[10, 20, 30, 40, 50, 60, 70, 80, 90].map((x) => (_jsx("line", { x1: `${x}%`, y1: "0", x2: `${x}%`, y2: "100%", stroke: "white", strokeWidth: "0.5" }, `v${x}`))), [10, 20, 30, 40, 50, 60, 70, 80, 90].map((y) => (_jsx("line", { x1: "0", y1: `${y}%`, x2: "100%", y2: `${y}%`, stroke: "white", strokeWidth: "0.5" }, `h${y}`)))] }), _jsx(Box, { sx: {
                                            position: 'absolute',
                                            bottom: 6,
                                            right: 8,
                                            px: 1,
                                            py: 0.25,
                                            bgcolor: 'rgba(0,0,0,0.5)',
                                            borderRadius: 1,
                                        }, children: _jsx(Typography, { sx: { fontSize: 9, color: 'rgba(255,255,255,0.7)' }, children: "Simulated Map \u2014 Click to place pond marker" }) }), _jsx(Box, { sx: {
                                            position: 'absolute',
                                            top: 10,
                                            right: 10,
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(0,0,0,0.5)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }, children: _jsx(Typography, { sx: { fontSize: 10, fontWeight: 800, color: 'white' }, children: "N\u2191" }) }), pinCoords && (_jsxs(Box, { sx: {
                                            position: 'absolute',
                                            left: '50%',
                                            top: '45%',
                                            transform: 'translate(-50%, -100%)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            pointerEvents: 'none',
                                        }, children: [_jsx(Box, { sx: {
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '50%',
                                                    bgcolor: colorTokens.error,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                                }, children: _jsx(Typography, { sx: { color: 'white', fontSize: 14 }, children: "\uD83D\uDCCD" }) }), _jsx(Box, { sx: { width: 2, height: 12, bgcolor: colorTokens.error } })] }))] }), _jsx(Box, { sx: { display: 'flex', gap: 1.5 }, children: [
                                    { label: 'Latitude', value: pinCoords ? `${pinCoords.lat.toFixed(4)}°N` : '—' },
                                    { label: 'Longitude', value: pinCoords ? `${pinCoords.lng.toFixed(4)}°E` : '—' },
                                    {
                                        label: 'Location',
                                        value: selectedLocationId
                                            ? (filteredLocations.find((l) => l.id === selectedLocationId)?.name ??
                                                searchQuery)
                                            : '—',
                                    },
                                ].map((item) => (_jsxs(Box, { sx: {
                                        flex: 1,
                                        bgcolor: colorTokens.gray50,
                                        border: `1px solid ${colorTokens.gray200}`,
                                        borderRadius: '12px',
                                        px: 1.5,
                                        py: 1.25,
                                    }, children: [_jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray600 }, children: item.label }), _jsx(Typography, { sx: {
                                                fontSize: 13,
                                                fontWeight: 700,
                                                color: colorTokens.gray800,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }, children: item.value })] }, item.label))) }), !pinCoords && (_jsx(Typography, { sx: {
                                    fontSize: 12,
                                    textAlign: 'center',
                                    color: colorTokens.yellow,
                                    fontWeight: 500,
                                }, children: "\u26A0 Click on the map to place your pond marker" }))] })), step === 2 && (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, p: 2.5 }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    borderRadius: '12px',
                                    px: 2,
                                    py: 1.5,
                                    bgcolor: `${colorTokens.secondary}10`,
                                    border: `1px solid ${colorTokens.secondary}30`,
                                }, children: [_jsx(Typography, { sx: { fontSize: 18, color: colorTokens.secondary }, children: "\uD83D\uDCCD" }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 700, color: colorTokens.secondary }, children: pinCoords
                                                    ? `${pinCoords.lat.toFixed(4)}°N, ${pinCoords.lng.toFixed(4)}°E`
                                                    : t('form.noCoordinates') }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray600 }, children: searchQuery || 'Custom location' })] }), _jsx(Box, { component: "button", onClick: () => setStep(1), sx: {
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: colorTokens.secondary,
                                            bgcolor: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                        }, children: t('form.change') })] }), _jsxs(Box, { children: [_jsxs(Typography, { component: "label", sx: {
                                            fontSize: 12,
                                            fontWeight: 700,
                                            display: 'block',
                                            mb: 0.75,
                                            color: colorTokens.gray800,
                                        }, children: [t('form.pondName'), ' ', _jsx(Box, { component: "span", sx: { color: colorTokens.error }, children: "*" })] }), _jsx(Box, { component: "input", value: pondName, onChange: (e) => setPondName(e.target.value), placeholder: t('form.pondNamePlaceholder'), maxLength: 50, sx: nativeInputSx })] }), _jsxs(Box, { children: [_jsxs(Typography, { component: "label", sx: {
                                            fontSize: 12,
                                            fontWeight: 700,
                                            display: 'block',
                                            mb: 0.75,
                                            color: colorTokens.gray800,
                                        }, children: [t('form.pondArea'), ' ', _jsx(Box, { component: "span", sx: { color: colorTokens.error }, children: "*" })] }), _jsx(Box, { component: "input", type: "number", value: pondArea, onChange: (e) => setPondArea(e.target.value), placeholder: t('form.pondAreaPlaceholder'), min: 0.01, step: 0.01, sx: nativeInputSx })] }), _jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.25,
                                    borderRadius: '12px',
                                    px: 2,
                                    py: 1.5,
                                    bgcolor: `${colorTokens.yellow}10`,
                                    border: `1px solid ${colorTokens.yellow}30`,
                                }, children: [_jsx(Typography, { sx: { color: colorTokens.yellow, fontSize: 16, mt: 0.1, flexShrink: 0 }, children: "\u3030" }), _jsxs(Typography, { sx: { fontSize: 12, color: colorTokens.orange }, children: [t('form.inactiveWarningPrefix'), ' ', _jsx(Box, { component: "strong", children: t('form.inactiveWarningBold') }), ' ', t('form.inactiveWarningSuffix')] })] }), error && _jsx(Alert, { severity: "error", children: t(error) })] }))] }), _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2,
                    borderTop: `1px solid ${colorTokens.gray200}`,
                    bgcolor: colorTokens.gray50,
                }, children: [_jsx(Box, { component: "button", onClick: step === 1 ? handleClose : () => setStep(1), sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 2,
                            py: 1,
                            borderRadius: '12px',
                            border: `1px solid ${colorTokens.gray200}`,
                            bgcolor: 'white',
                            color: colorTokens.gray600,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                        }, children: step === 1 ? '✕ Cancel' : '← Back' }), step === 1 ? (_jsx(Box, { component: "button", onClick: () => setStep(2), disabled: !canProceedStep1, sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 2.5,
                            py: 1,
                            borderRadius: '12px',
                            border: 'none',
                            bgcolor: canProceedStep1 ? colorTokens.primary : colorTokens.gray300,
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: canProceedStep1 ? 'pointer' : 'not-allowed',
                        }, children: "Next \u2192" })) : (_jsxs(Box, { component: "button", onClick: handleSave, disabled: !canSave, sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 2.5,
                            py: 1,
                            borderRadius: '12px',
                            border: 'none',
                            bgcolor: canSave ? colorTokens.secondary : colorTokens.gray300,
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: canSave ? 'pointer' : 'not-allowed',
                        }, children: [isSaving ? _jsx(CircularProgress, { size: 12, sx: { color: 'white' } }) : null, isSaving ? 'Saving…' : t('form.save')] }))] })] }));
});
